import React from "react";
import { X } from "lucide-react";

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-[#402B69]">Privacy Policy</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4">
          <p>
            Glossa respects your privacy and is committed to protecting personal data
            processed through our platform. This policy explains what we collect, why we collect
            it, and how we use it.
          </p>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">1. Data We Collect</h3>
            <p>
              We collect business account details, user profile information, customer records
              entered by your business, appointment and transaction data, support communications,
              and technical logs required to operate and secure the service.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">2. How We Use Data</h3>
            <p>
              Data is used to provide and improve Glossa services, process payments, deliver
              customer support, maintain security, comply with legal obligations, and communicate
              important service updates related to your subscription.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">3. Legal Basis</h3>
            <p>
              We process data where necessary to perform our contract with you, comply with legal
              obligations, pursue legitimate business interests such as fraud prevention, or where
              consent has been provided where required.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">4. Data Sharing</h3>
            <p>
              Glossa may share data with trusted subprocessors such as payment providers,
              hosting partners, and communication services solely to deliver the platform.
              We do not sell personal data.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">5. Security & Retention</h3>
            <p>
              We apply administrative, technical, and organisational safeguards to protect data.
              Information is retained only for as long as needed to provide services, meet legal
              requirements, or resolve disputes.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">6. Your Rights</h3>
            <p>
              Depending on your location, you may have rights to access, correct, delete, restrict,
              or export personal data, and to object to certain processing. Contact us to exercise
              these rights.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">7. Contact</h3>
            <p>
              For privacy enquiries, contact Glossa at support@getglossa.com. We will respond within
              a reasonable timeframe in line with applicable data protection law.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#885ABB] hover:bg-[#7647a8] text-white rounded-lg font-semibold text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
